import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Trash2, X, Sparkles, AlertTriangle, Loader2 } from "lucide-react";

export interface PopupProps {
  Delete?: () => void;
  onConfirm?: () => void;
  cancel?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  text?: React.ReactNode;
  message?: React.ReactNode;
  buttonText?: string;
  confirmText?: string;
  isDeleting?: boolean;
  isLoading?: boolean;
  customIcon?: React.ReactNode;
  title?: string;
  badgeText?: string;
  warningText?: React.ReactNode;
  confirmIcon?: React.ReactNode;
  variant?: "gradient" | "danger";
  isOpen?: boolean;
}

export const DeletePopup: React.FC<PopupProps> = ({
  Delete,
  onConfirm,
  cancel,
  onClose,
  onCancel,
  text,
  message,
  buttonText = "Delete",
  confirmText,
  isDeleting = false,
  isLoading = false,
  customIcon,
  title = "Please confirm",
  badgeText = "Confirm action",
  warningText = "This action cannot be undone easily.",
  confirmIcon,
  variant = "gradient",
  isOpen = true,
}) => {
  if (isOpen === false) return null;

  const handleConfirmAction = () => {
    if (Delete) Delete();
    else if (onConfirm) onConfirm();
  };

  const handleCancelAction = () => {
    if (cancel) cancel();
    else if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  const loading = isDeleting || isLoading;
  const contentText = text ?? message;
  const finalBtnText = confirmText ?? buttonText;

  return createPortal(
    <Overlay
      onClick={(e) => {
        e.stopPropagation();
        if (!loading) handleCancelAction();
      }}
    >
      <Popup onClick={(e) => e.stopPropagation()}>
        <Hero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              {customIcon ? customIcon : <Trash2 size={22} />}
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                {badgeText}
              </HeroBadge>
              <Title>{title}</Title>
              {contentText && <Subtitle>{contentText}</Subtitle>}
            </HeroText>
            <CloseBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!loading) handleCancelAction();
              }}
              aria-label="Close"
              disabled={loading}
            >
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </Hero>

        <Body>
          {warningText && (
            <WarningChip $severity={variant === "danger" ? "danger" : "warning"}>
              <AlertTriangle size={14} />
              <span>{warningText}</span>
            </WarningChip>
          )}
          <ActionRow>
            <CancelButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!loading) handleCancelAction();
              }}
              disabled={loading}
            >
              Cancel
            </CancelButton>
            <ConfirmButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!loading) handleConfirmAction();
              }}
              disabled={loading}
              $variant={variant}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>
                    {finalBtnText.toLowerCase().includes("delete")
                      ? "Deleting..."
                      : `${finalBtnText}...`}
                  </span>
                </>
              ) : (
                <>
                  {confirmIcon !== undefined ? (
                    confirmIcon
                  ) : finalBtnText.toLowerCase().includes("delete") ? (
                    <Trash2 size={16} />
                  ) : null}
                  <span>{finalBtnText}</span>
                </>
              )}
            </ConfirmButton>
          </ActionRow>
        </Body>
      </Popup>
    </Overlay>,
    document.body
  );
};

export const AlertModal = DeletePopup;
export default DeletePopup;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  padding: 16px;
`;

const Popup = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  animation: slideUp 0.18s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);

  img,
  svg {
    max-width: 22px;
    max-height: 22px;
  }
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const Subtitle = styled.div`
  margin: 6px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  line-height: 1.45;

  strong {
    color: #171717;
    font-weight: 700;
  }
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Body = styled.div`
  padding: 20px 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarningChip = styled.div<{ $severity?: "warning" | "danger" }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${({ $severity }) =>
    $severity === "danger"
      ? "rgba(239, 68, 68, 0.1)"
      : "rgba(240, 154, 62, 0.1)"};
  border: 1px solid
    ${({ $severity }) =>
      $severity === "danger"
        ? "rgba(239, 68, 68, 0.22)"
        : "rgba(240, 154, 62, 0.22)"};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $severity }) =>
    $severity === "danger" ? "#ef4444" : "#f09a3e"};
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button<{ $variant?: "gradient" | "danger" }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${({ $variant }) =>
    $variant === "danger" ? "#ef4444" : gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${({ $variant }) =>
    $variant === "danger"
      ? "0 8px 20px rgba(239, 68, 68, 0.25)"
      : "0 8px 20px rgba(201, 77, 170, 0.25)"};
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    ${({ $variant }) =>
      $variant === "danger"
        ? "background: #dc2626;"
        : "transform: translateY(-1px);"}
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
